import numeral from 'numeral'
import {
  MonetaryAmountFragment,
  SellablePricingFragment,
  SellableType,
  SellableUrlFragment
} from '#src/generated/graphql'
import { formatMonetaryAmountAsJsx, MonetaryAmountFormattingOptions } from './money'

interface Sellable {
  type: string
  title?: string
  brand: {
    name: string
    displayName: string
  } | null
  serviceLocation: {
    displayTitle: string | null
  } | null
}

export function getSellableUrl(sellableUrlData: SellableUrlFragment) {
  if (
    sellableUrlData.type !== SellableType.PRODUCT &&
    sellableUrlData.type !== SellableType.SERVICE
  ) {
    throw new Error(
      `Attempted to generate link for unhandled sellable type [${sellableUrlData.type}]`
    )
  }

  let sellableUrl = '/'

  // First determine if this is a product or experience
  sellableUrl += `${sellableUrlData.type === SellableType.PRODUCT ? 'products' : 'experiences'}`

  // If a friendly ID is available, use that, otherwise use the default sellable ID
  sellableUrl += `/${sellableUrlData.friendlyId ? sellableUrlData.friendlyId : sellableUrlData.id}`

  // Slug should always be available, append this to the end in any case
  sellableUrl += `/${sellableUrlData.slug}`

  return sellableUrl
}

/**
 * Returns a title and subtitle of a Sellable based on their type.
 *
 * This function is useful to have consistency data across the website.
 *
 * @param sellable
 */
export function getTitleAndSubtitle(
  sellable: Sellable
): { title?: string; subtitle?: string | null } {
  if (sellable.type === 'SERVICE') {
    return {
      title: sellable.title,
      subtitle: sellable.serviceLocation ? sellable.serviceLocation.displayTitle : undefined
    }
  }

  return {
    title: sellable.brand ? sellable.brand.displayName : undefined,
    subtitle: sellable.title
  }
}

interface FormattedSellablePricing {
  price: string | JSX.Element
  msrp?: string | JSX.Element
  savings?: string | JSX.Element
  savingsPercentage?: string
}

export function formatSellablePricing(
  sellable: SellablePricingFragment,
  formattingOptions?: MonetaryAmountFormattingOptions & {
    percentSavingsDisplayThreshold?: number
  },
  normalizationOptions?: NormalizationOptions
): FormattedSellablePricing | null {
  const pricing = normalizeSellablePricing(sellable, normalizationOptions)

  if (!pricing) {
    return null
  }

  const { price, msrp, savings, savingsPercentage } = pricing

  // The savings display threshold is passed in as a whole number
  const displaySavings =
    formattingOptions &&
    formattingOptions.percentSavingsDisplayThreshold &&
    savingsPercentage &&
    savingsPercentage * 100 < formattingOptions!.percentSavingsDisplayThreshold!
      ? false
      : true

  return {
    price: formatMonetaryAmountAsJsx(price, formattingOptions),
    msrp: msrp && displaySavings ? formatMonetaryAmountAsJsx(msrp, formattingOptions) : undefined,
    savings:
      savings && displaySavings ? formatMonetaryAmountAsJsx(savings, formattingOptions) : undefined,
    savingsPercentage:
      savingsPercentage && displaySavings ? `${Math.round(savingsPercentage * 100)}%` : undefined
  }
}

/**
 * Normalize a Sellable's pricing information
 *
 * This function assumes that the salePrice and originalPrice have the same currency
 */
interface SellablePricing {
  price: MonetaryAmountFragment
  msrp?: MonetaryAmountFragment
  savings?: MonetaryAmountFragment
  savingsPercentage?: number
}

interface NormalizationOptions {
  preferDefaultVariant?: boolean
}

export function normalizeSellablePricing(
  sellable: SellablePricingFragment,
  options: NormalizationOptions = {}
): SellablePricing | null {
  let { salePrice, originalPrice } = sellable

  /**
   * When rendering parent sellables for things like product cards it's best to use the pricing
   *   from the `defaultVariant` in the variation matrix.
   */
  if (options.preferDefaultVariant && sellable.variationMatrix) {
    salePrice = sellable.variationMatrix.defaultVariant.salePrice
    originalPrice = sellable.variationMatrix.defaultVariant.originalPrice
  }

  if (salePrice && originalPrice && salePrice.amount !== originalPrice.amount) {
    const savings = parseFloat(originalPrice.amount) - parseFloat(salePrice.amount)
    const percentSavings = savings / parseFloat(originalPrice.amount)

    // If there is both a sale price and an orignal price (MSRP), return both
    return {
      price: salePrice,
      msrp: originalPrice,
      savings: {
        __typename: 'MonetaryAmount',
        amount: numeral(savings).format('0,0.00'),
        amountInSmallestDenomination: `${savings * 100}`,
        currency: salePrice.currency
      },
      savingsPercentage: percentSavings
    }
  } else if (salePrice) {
    // Otherwise, return just the sale price
    return {
      price: salePrice
    }
  } else {
    // If there is no pricing information, return null
    return null
  }
}
